import clsx from 'clsx';
import { ArrowRightLeft, ExternalLink, Phone, PhoneIncoming, PhoneMissed, PhoneOutgoing, StickyNote } from 'lucide-react';
import { useState } from 'react';
import { TeamBadge } from '../../components/TeamBadge';
import { Badge, Modal, Spinner } from '../../components/ui';
import { dateTimeOf, formatDuration, statusLabel, timeOf } from '../../lib/format';
import { ticketLabel, useAttendance, useMessages, useOperators, useTicketLink } from '../../lib/queries';
import { AttendanceItem, CallHistoryItem } from '../../lib/types';
import { MessageList } from './MessageList';

const TONE: Record<string, 'ok' | 'neutral' | 'danger' | 'accent' | 'warn'> = {
  completed: 'ok',
  cancelled: 'neutral',
  spam: 'danger',
};

/**
 * Linha do tempo do contato: todos os atendimentos, do mais recente para o mais antigo, com
 * operador, equipe, chamado, tempo efetivo e encerramento. A conversa de um atendimento anterior
 * abre por cima, só para leitura, sem sair do atendimento atual (o foco continua contando aqui).
 */
export function ContactTimeline({ history, calls = [], currentId }: { history: AttendanceItem[]; calls?: CallHistoryItem[]; currentId: string }) {
  const [viewing, setViewing] = useState<AttendanceItem | null>(null);
  // Ligações que viraram atendimento aparecem dentro dele; as outras (perdidas, sem atendimento)
  // entram na linha do tempo na ordem em que aconteceram.
  const callsOf = (attendanceId: string) => calls.filter((c) => c.attendanceId === attendanceId);
  const entries = [
    ...history.map((h) => ({ kind: 'attendance' as const, at: h.createdAt, item: h })),
    ...calls.filter((c) => !c.attendanceId || !history.some((h) => h.id === c.attendanceId)).map((c) => ({ kind: 'call' as const, at: c.startedAt, item: c })),
  ].sort((x, y) => y.at.localeCompare(x.at));
  return (
    <>
      <ol className="relative ml-1.5 space-y-3 border-l border-border pl-4">
        {entries.map((e) => {
          if (e.kind === 'call') return <CallEntry key={e.item.id} call={e.item} />;
          const h = e.item;
          const current = h.id === currentId;
          const linkedCalls = callsOf(h.id);
          return (
            <li key={h.id} className="relative">
              <span
                className={clsx(
                  'absolute top-1.5 -left-[21px] size-2.5 rounded-full border-2 border-panel',
                  current ? 'bg-accent' : h.status !== 'finalizado' ? 'bg-warn' : h.finishReason === 'completed' ? 'bg-ok' : 'bg-fg-soft',
                )}
              />
              <button
                type="button"
                disabled={current}
                onClick={() => setViewing(h)}
                className={clsx('w-full rounded-md px-2 py-1 text-left text-xs', current ? 'cursor-default bg-accent/10' : 'hover:bg-muted')}
                title={current ? 'Atendimento aberto agora' : 'Ver a conversa deste atendimento'}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{dateTimeOf(h.createdAt)}</span>
                  {current ? <Badge tone="accent">este</Badge> : <Badge tone={h.finishReason ? TONE[h.finishReason] : 'warn'}>{statusLabel(h)}</Badge>}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-fg-soft">
                  {h.teamName && <TeamBadge name={h.teamName} />}
                  <span>{h.operatorName ?? 'sem operador'}</span>
                  {h.ticketId && <span>· {ticketLabel(h)}</span>}
                  {h.totalSeconds > 0 && <span className="font-mono">· {formatDuration(h.totalSeconds)}</span>}
                </div>
                {linkedCalls.length > 0 && (
                  <div className="mt-0.5 flex items-center gap-1 text-fg-soft">
                    <Phone className="size-3" />
                    {linkedCalls.length === 1 ? '1 ligação' : `${linkedCalls.length} ligações`}
                    {' · '}
                    {formatDuration(linkedCalls.reduce((sum, c) => sum + (c.talkSeconds ?? 0), 0))} em conversa
                  </div>
                )}
                {!current && h.lastMessagePreview && <div className="mt-0.5 truncate text-fg-soft italic">{h.lastMessagePreview}</div>}
              </button>
            </li>
          );
        })}
      </ol>
      {viewing && <PastAttendanceModal item={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

/** Ligação sem atendimento (perdida, ou atendida sem virar atendimento) na linha do tempo. */
function CallEntry({ call: c }: { call: CallHistoryItem }) {
  const missed = c.result === 'missed';
  const ongoing = c.state !== 'ended';
  const Icon = missed ? PhoneMissed : c.direction === 'outbound' ? PhoneOutgoing : PhoneIncoming;
  const label = ongoing ? 'Ligação em andamento' : missed ? (c.direction === 'outbound' ? 'Ligação não atendida' : 'Ligação perdida') : c.direction === 'outbound' ? 'Ligação feita' : 'Ligação recebida';
  return (
    <li className="relative">
      <span className={clsx('absolute top-1.5 -left-[21px] size-2.5 rounded-full border-2 border-panel', missed ? 'bg-danger' : 'bg-fg-soft')} />
      <div className="rounded-md px-2 py-1 text-xs">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium">{dateTimeOf(c.startedAt)}</span>
          <span className={clsx('inline-flex items-center gap-1', missed ? 'text-danger' : 'text-fg-soft')}>
            <Icon className="size-3.5" /> {label}
          </span>
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-1.5 text-fg-soft">
          {c.operatorName && <span>{c.operatorName}</span>}
          {c.waitSeconds !== null && <span>· esperou {formatDuration(c.waitSeconds)}</span>}
          {c.talkSeconds !== null && <span className="font-mono">· {formatDuration(c.talkSeconds)}</span>}
        </div>
      </div>
    </li>
  );
}

/** Conversa e registro de um atendimento anterior, só leitura. */
function PastAttendanceModal({ item, onClose }: { item: AttendanceItem; onClose: () => void }) {
  const details = useAttendance(item.id).data;
  const messages = useMessages(item.conversationId, item.id);
  const operators = useOperators().data ?? [];
  const ticketLink = useTicketLink(item.ticketId);
  const nameOf = (id: string | null) => (id ? (operators.find((o) => o.id === id)?.name ?? 'operador') : 'fila');

  return (
    <Modal open title={`Atendimento de ${dateTimeOf(item.createdAt)}`} onClose={onClose} width="max-w-3xl">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={item.finishReason ? TONE[item.finishReason] : 'warn'}>{statusLabel(item)}</Badge>
        {item.teamName && <TeamBadge name={item.teamName} />}
        <span>{item.operatorName ?? 'sem operador'}</span>
        <span className="text-fg-soft">· {item.channelName}</span>
        {item.ticketId &&
          (ticketLink ? (
            <a href={ticketLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-accent hover:underline">
              {ticketLabel(item)} <ExternalLink className="size-3" />
            </a>
          ) : (
            <span className="font-medium">{ticketLabel(item)}</span>
          ))}
        <span className="ml-auto font-mono text-xs text-fg-soft" title="Tempo efetivo (soma das sessões)">
          {formatDuration(item.totalSeconds)}
        </span>
      </div>
      {item.finishedAt && <p className="mb-2 text-xs text-fg-soft">Encerrado em {dateTimeOf(item.finishedAt)}</p>}
      {details?.summary && (
        <p className="mb-2 flex gap-1.5 rounded-md bg-muted px-3 py-2 text-xs whitespace-pre-wrap">
          <StickyNote className="mt-0.5 size-3.5 shrink-0" /> {details.summary}
        </p>
      )}
      {details && details.transfers.length > 0 && (
        <ul className="mb-3 space-y-1 text-xs">
          {details.transfers.map((t, i) => (
            <li key={i} className="flex gap-1.5">
              <ArrowRightLeft className="mt-0.5 size-3.5 shrink-0 text-fg-soft" />
              <span>
                <span className="text-fg-soft">{timeOf(t.createdAt)}</span> {nameOf(t.fromUserId)} → {nameOf(t.toUserId)}
                {t.note && <span className="text-fg-soft">: {t.note}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="h-[55vh] overflow-hidden rounded-md border border-border">
        {messages.isLoading ? <div className="flex h-full items-center justify-center"><Spinner /></div> : <MessageList messages={messages.data ?? []} loading={false} attendanceId={item.id} />}
      </div>
      <p className="mt-2 text-[11px] text-fg-soft">Somente leitura. Notas internas aparecem com o cadeado.</p>
    </Modal>
  );
}
